<template>
  <div>
    <div class="filter-form">
      <el-form :inline="false" :size="size" label-width="220px" :label-position="$device.mobile ? 'top' : 'right'">
        <el-form-item :label="$t('검색 기간 (qr2 주문 0건)')">
          <el-radio-group v-model="searchForm.months" size="small">
            <el-radio-button :label="1">{{ $t('1개월') }}</el-radio-button>
            <el-radio-button :label="2">{{ $t('2개월') }}</el-radio-button>
            <el-radio-button :label="3">{{ $t('3개월') }}</el-radio-button>
            <el-radio-button :label="6">{{ $t('6개월') }}</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <div class="exclusion-block">
          <div class="block-title">
            {{ $t('운영 준비된 매장 결과에서 제외 (옵션)') }}
            <span class="block-hint">{{ $t('체크된 조건을 만족하는 매장은 결과에서 제외됩니다.') }}</span>
          </div>

          <el-form-item :label="$t('신규 매장 (가입 후) 제외')">
            <el-input-number
              v-model="searchForm.excludeNewerThanMonths"
              :min="0"
              :max="24"
              size="small"
              style="width: 130px"></el-input-number>
            <span class="suffix">{{ $t('개월 이내 매장') }}</span>
            <span class="field-hint">{{ $t('0이면 신규 매장 제외 안 함') }}</span>
          </el-form-item>

          <div class="checkbox-grid">
            <el-checkbox v-model="searchForm.excludeIfHasMenu">{{ $t('메뉴가 등록된 매장 제외') }}</el-checkbox>
            <el-checkbox v-model="searchForm.excludeIfHasImage">{{ $t('매장 사진이 등록된 매장 제외') }}</el-checkbox>
            <el-checkbox v-model="searchForm.excludeIfPgConfigured">{{ $t('PG가 설정된 매장 제외') }}</el-checkbox>
            <el-checkbox v-model="searchForm.excludeIfMidIsNotTest">
              {{ $t('MID가 정상(test 미포함)인 매장 제외') }}
            </el-checkbox>
            <el-checkbox v-model="searchForm.excludeIfHasLicenseKey">
              {{ $t('라이센스 키가 있는 매장 제외') }}
            </el-checkbox>
          </div>
        </div>

        <div class="actions">
          <el-button @click="onSearch" icon="el-icon-search" type="primary" :size="size">{{ $t('검색') }}</el-button>
          <el-button @click="bulkHide(true)" :disabled="selected.length === 0" type="warning" :size="size">
            {{ $t('선택 매장 오더톡 보이지 않기 ON') }} ({{ selected.length }})
          </el-button>
          <el-button @click="bulkHide(false)" :disabled="selected.length === 0" :size="size">
            {{ $t('선택 매장 오더톡 보이지 않기 OFF') }}
          </el-button>
        </div>
      </el-form>
    </div>

    <!-- 선택은 **페이지를 넘어 남는다**(`row-key` + `reserve-selection`) — 한 장에 300건이라도 그 장이
         일괄 처리의 범위가 되면, 1페이지에서 고른 매장이 2페이지로 넘어가는 순간 조용히 빠진다.
         고른 것을 버리는 시점은 조건이 바뀐 검색과 일괄 처리 성공 두 번뿐이다([onSearch]·[bulkHide]) -->
    <el-table
      ref="table"
      :data="tableData"
      row-key="storeCd"
      @selection-change="onSel"
      :height="tableHeight"
      :size="size"
      stripe
      :empty-text="$t('검색된 비활성 매장이 없습니다.')">
      <el-table-column type="selection" width="50" reserve-selection></el-table-column>
      <el-table-column prop="storeId" :label="$t('매장 ID')" width="120"></el-table-column>
      <el-table-column prop="storeNm" :label="$t('매장명')" min-width="160"></el-table-column>
      <el-table-column prop="custNm" :label="$t('사업장')" min-width="140">
        <template slot-scope="s">{{ s.row.custNm || '-' }}</template>
      </el-table-column>
      <el-table-column :label="$t('가입일')" width="120">
        <template slot-scope="s">{{ formatDate(s.row.startDt) }}</template>
      </el-table-column>
      <el-table-column :label="$t('PG/MID')" min-width="180">
        <template slot-scope="s">
          <span v-if="s.row.pgKind">
            {{ s.row.pgKind }}
            <span style="color: #909399">/ {{ s.row.mid || '-' }}</span>
          </span>
          <el-tag v-else type="warning" size="mini">{{ $t('미설정') }}</el-tag>
        </template>
      </el-table-column>
      <!-- 상점키 원문을 실어 보내고 화면이 「있나」를 보던 것을 서버의 같은 판정([hasMerchantKey])으로 대신한다
           (R-D2 S-1) — 이 목록에 필요한 것은 그 불리언 하나이고, 원문은 수정 화면 뷰에만 실린다 -->
      <el-table-column prop="hasMerchantKey" :label="$t('라이센스')" width="100">
        <template slot-scope="s">
          <el-tag v-if="s.row.hasMerchantKey" size="mini" type="success">{{ $t('있음') }}</el-tag>
          <el-tag v-else size="mini" type="warning">{{ $t('없음') }}</el-tag>
        </template>
      </el-table-column>
      <!-- 이미지 6칸(`storeImg`·`storeImg1`~`5`)을 전부 실어 보내고 화면이 「하나라도 있나」를 셈하던 것을
           서버가 같은 판정을 한 [hasImage] 로 대신한다 — 목록에 필요한 것은 그 불리언 하나뿐이다 -->
      <el-table-column prop="hasImage" :label="$t('이미지')" width="100">
        <template slot-scope="s">
          <el-tag v-if="s.row.hasImage" size="mini" type="success">{{ $t('있음') }}</el-tag>
          <el-tag v-else size="mini" type="warning">{{ $t('없음') }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column :label="$t('현재 노출')" width="100">
        <template slot-scope="s">
          <el-tag :type="s.row.hideInOrdertalk ? 'info' : 'success'" size="mini">
            {{ s.row.hideInOrdertalk ? $t('숨김') : $t('노출') }}
          </el-tag>
        </template>
      </el-table-column>
    </el-table>
    <list-pagination
      :search-form="searchForm"
      :total="totalElements"
      :page-sizes="[50, 100, 200, 300]"
      @page-change="handlePageChange"
      @size-change="handleSizeChange"></list-pagination>
  </div>
</template>

<script>
import mainVueMixin from '@/mixins/mainVueMixin'
import pagedListMixin from '@/mixins/pagedListMixin'
import { withPaging } from '@/utils/pagedList'
import { mapGetters } from 'vuex'

/** 서버가 한 번에 받는 매장 수(`StoreController.BULK_HIDE_MAX`) — 넘기면 400 이라 화면이 먼저 막는다 */
const BULK_HIDE_MAX = 500

export default {
  mixins: [mainVueMixin, pagedListMixin],
  data() {
    return {
      size: 'small',
      // 이 패널의 조건은 `searchForm`·결과는 `tableData` 라는 이름을 쓴다 — [pagedListMixin] 과
      // `<list-pagination>` 이 그 두 이름으로 페이지를 옮기고 건수를 읽는다(다른 목록 화면과 같은 어휘)
      //
      // 한 장을 300건으로 여는 이유: 이 패널은 고른 매장을 **한 번에** 처리하는 화면이고, 기본 50건은
      // 1,156건짜리 결과를 스물세 번 넘겨 가며 고르게 만든다. 선택은 페이지를 넘어 남지만(`reserve-selection`)
      // 한 장이 넓을수록 그럴 일 자체가 줄어든다.
      searchForm: withPaging(
        {
          months: 1,
          excludeNewerThanMonths: 3,
          excludeIfHasMenu: false,
          excludeIfHasImage: false,
          excludeIfPgConfigured: false,
          excludeIfMidIsNotTest: false,
          excludeIfHasLicenseKey: false,
        },
        { pageSize: 300 }
      ),
      tableData: [],
      selected: [],
    }
  },
  computed: {
    ...mapGetters({
      tableHeight: 'ui/tableHeight',
    }),
  },
  methods: {
    /**
     * 조회 — 페이징·정렬 축은 **본문**에 실린다(이 검색만 POST 다).
     *
     * [searchQuery] 가 정렬 축이 빈 두 키를 빼 주므로 서버는 그때 자기 기본 정렬로 답한다 —
     * 이 표에는 아직 정렬 열이 없으므로 언제나 그 갈래다.
     */
    search() {
      this.mainVue.$axios.post(
        '/store/inactive/search',
        this.searchQuery(),
        function (result) {
          this.applyPage(result)
        }.bind(this)
      )
    },
    /**
     * 조건이 바뀐 검색 — **고른 것을 먼저 버린다**.
     *
     * 선택이 페이지를 넘어 남는다는 것은 조건을 바꿔도 남는다는 뜻이다. 새 조건의 결과에 없는 매장이
     * 선택에 남아 있으면, 화면에 보이지 않는 매장이 일괄 처리에 딸려 간다.
     */
    onSearch() {
      this.clearSelection()
      this.searchFirstPage()
    },
    clearSelection() {
      this.selected = []

      if (this.$refs.table) this.$refs.table.clearSelection()
    },
    onSel(sel) {
      this.selected = sel
    },
    formatDate(d) {
      return d ? this.util.getDate('yyyy-MM-dd', new Date(d)) : '-'
    },
    bulkHide(hide) {
      const cds = this.selected.map((s) => s.storeCd)
      if (cds.length === 0) return

      // 서버가 거절할 요청은 보내지 않는다 — 400 을 받고 나서야 「너무 많다」를 알면 고른 것을 다시 줄여야 한다
      if (cds.length > BULK_HIDE_MAX) {
        this.mainVue.$message({
          type: 'warning',
          message: `${this.$t('한 번에 처리 가능한 매장 수는')} ${BULK_HIDE_MAX}${this.$t('개 이하입니다.')} (${this.$t(
            '현재 선택'
          )} ${cds.length})`,
        })

        return
      }

      const action = hide ? this.$t('숨김') : this.$t('노출')
      const confirmMsg = `${cds.length}${this.$t('개 매장의 오더톡 노출을')} ${action}${this.$t('으로 변경합니다.')}`
      this.mainVue
        .$confirm(confirmMsg, this.$t('확인'), {
          type: 'warning',
          confirmButtonText: this.$t('확인'),
          cancelButtonText: this.$t('취소'),
        })
        .then(() => {
          this.mainVue.$axios.add(
            '/store/hideInOrdertalk/bulk',
            { storeCds: cds, hide: hide },
            {},
            function (result) {
              const updated = (result && result.updated) || 0
              const requested = (result && result.requested) || cds.length
              const missing = (result && result.missingStoreCds) || []
              let msg = `${this.$t('일괄 처리 완료')} (${updated}/${requested})`
              if (missing.length > 0) {
                msg += ` — ${this.$t('미처리 매장 코드')}: ${missing.join(', ')}`
              }
              this.mainVue.$message({ type: 'success', message: msg })
              // 처리한 매장은 이미 다룬 것이다 — 남겨 두면 다음 일괄 처리에 그대로 딸려 간다
              this.clearSelection()
              this.search()
            }.bind(this)
          )
        })
        .catch(() => {})
    },
  },
}
</script>

<style scoped>
.filter-form {
  margin-bottom: 12px;
}
.exclusion-block {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 12px 16px 4px 16px;
  margin: 8px 0;
  background: #fafafa;
}
.block-title {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
}
.block-hint {
  font-weight: normal;
  font-size: 12px;
  color: #909399;
  margin-left: 8px;
}
.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 8px 16px;
  padding: 0 0 8px 224px;
}
.actions {
  margin-top: 8px;
  padding-left: 224px;
}
.suffix {
  margin-left: 8px;
  color: #606266;
}
.field-hint {
  margin-left: 12px;
  color: #909399;
  font-size: 12px;
}
@media (max-width: 768px) {
  .checkbox-grid,
  .actions {
    padding-left: 0;
  }
}
</style>
